import {
  Box,
  Button,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from "@mui/material";
import { useNavigate } from "react-router-dom";

import AddIcon from "@mui/icons-material/Add";
import { useState, useMemo, useEffect } from "react";

import React from "react";
import SearchBar from "../../components/SearchBar";
import SwitchToggle from "../../components/SwitchToggle";
import AddProductCategory from "../../Modals/addcategory";
import DeleteConfirmationModal from "../../Modals/deleteConfirmation";
import { useGetCategoryListQuery, useDeleteCategoryMutation, useUpdateCategoryStatusMutation, CategoryItem } from "../../store/api/manageAPI";
import { showToast, showError } from "../../constants/toasts";
import Loader from "../../constants/Loader";
import Pagination from "../../components/Panigation";


const ManageProductCategories = () => {
  const navigate = useNavigate();
  const [searchTerm, setSearchTerm] = useState("");
  const [page, setPage] = useState(1);
  const limit = 10;
  const [openModal, setOpenModal] = useState(false);
  const [editCategory, setEditCategory] = useState<CategoryItem | null>(null);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [categoryToDelete, setCategoryToDelete] = useState<{ id: string; name: string } | null>(null);
  
  // Reset to first page when search changes
  useEffect(() => {
    setPage(1);
  }, [searchTerm]);

  // Fetch categories from API
  const { data, isLoading, error, refetch } = useGetCategoryListQuery({
    page,
    limit,
    search: searchTerm,
  });

  // Delete category mutation
  const [deleteCategory, { isLoading: isDeleting }] = useDeleteCategoryMutation();

  // Update category status mutation
  const [updateCategoryStatus, { isLoading: isUpdatingStatus }] = useUpdateCategoryStatusMutation();

  const handleCloseModal = () => {
    setOpenModal(false);
    setEditCategory(null);
    refetch();
  };

  const handleEditClick = (category: any) => {
    const categoryData: CategoryItem = {
      _id: category.id,
      id: category.id,
      name: category.name,
      image: category.image,
      isBlocked: category.isBlocked,
    };
    setEditCategory(categoryData);
    setOpenModal(true);
  };

  const handleDeleteClick = (category: any) => {
    setCategoryToDelete({ id: category.id, name: category.name });
    setDeleteModalOpen(true);
  };

  const handleConfirmDelete = async () => {
    if (!categoryToDelete) return;

    try {
      const response = await deleteCategory({ id: categoryToDelete.id }).unwrap();
      const successMessage = response?.message || `Category "${categoryToDelete.name}" deleted successfully`;
      showToast(successMessage);
      setDeleteModalOpen(false);
      setCategoryToDelete(null);
      refetch();
    } catch (error: any) {
      console.error("Error deleting category:", error);
      const errorMessage =
        error?.data?.message ||
        error?.error?.data?.message ||
        error?.message ||
        "Failed to delete category. Please try again.";
      showError(errorMessage);
    }
  };

  // Handle status toggle (isBlocked)
  const handleStatusToggle = async (categoryId: string, categoryName: string, currentStatus: boolean) => {
    const newStatus = !currentStatus; // isBlocked: false means active, true means blocked
    
    try {
      const response = await updateCategoryStatus({ 
        id: categoryId, 
        isBlocked: newStatus 
      }).unwrap();
      
      // Show success message from API response or default message
      const successMessage = response?.message || 
        `Category "${categoryName}" ${newStatus ? "blocked" : "unblocked"} successfully`;
      showToast(successMessage);
      
      // Refetch the list to get updated data
      refetch();
    } catch (error: any) {
      console.error("Error updating category status:", error);
      
      // Extract error message from API response or show default
      const errorMessage =
        error?.data?.message ||
        error?.error?.data?.message ||
        error?.message ||
        "Failed to update category status. Please try again.";
      
      showError(errorMessage);
      
      // Refetch to revert the UI state if update failed
      refetch();
    }
  };

  // Extract categories from API response (excluding subcategories)
  const rows = useMemo(() => {
    if (data?.data?.list && Array.isArray(data.data.list)) {
      return data.data.list.map((category: any) => {
        // Exclude subcategories from the mapped data
        const { subcategories, subcategoryCount, ...categoryData } = category;
        return {
          id: categoryData._id || categoryData.id,
          name: categoryData.name || "N/A",
          image: categoryData.image || "/static/images/placeholder.png",
          isBlocked: categoryData.isBlocked ?? false,
          createdAt: categoryData.createdAt
            ? new Date(categoryData.createdAt).toLocaleDateString("en-GB", {
                day: "2-digit",
                month: "short",
                year: "numeric",
              })
            : "N/A",
        };
      });
    }
    return [];
  }, [data]);

  // Calculate total pages
  const totalCount = data?.data?.totalCount || 0;
  const totalPages = Math.ceil(totalCount / limit);

  // Handle page change
  const handlePageChange = (newPage: number) => {
    if (newPage >= 1 && newPage <= totalPages) {
      setPage(newPage);
    }
  };

  return (
    <>
      <Loader isLoad={isLoading || isDeleting || isUpdatingStatus} />
      <div className="main_title">
        <h1>Manage Product Categories</h1>
        <p>
          <button onClick={() => navigate("/dashboard")}>Dashboard</button> - Manage Product Categories
        </p>
      </div>

      <div className="cards">
        <div className="table_header">
          <div className="left_s">
            <SearchBar
              value={searchTerm}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => setSearchTerm(e.target.value)}
              onCross={() => setSearchTerm("")}
              searchTerm={searchTerm}
              setDebouncedSearchTerm={setSearchTerm}
              placeholder="Search..."
            />
          </div>
          <div className="right_s">
            <Button
              className="btn btn_primary"
              onClick={() => {
                setEditCategory(null);
                setOpenModal(true);
              }}
            >
              <AddIcon /> Add Product Category
            </Button>
          </div>
        </div>

        <TableContainer className="table_container">
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Sr. No</TableCell>
                <TableCell>Category</TableCell>
                <TableCell>Created Date</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Actions</TableCell>
              </TableRow>
            </TableHead>

            <TableBody>
              {isLoading ? (
                <TableRow>
                  <TableCell colSpan={5} align="center">
                    Loading...
                  </TableCell>
                </TableRow>
              ) : error ? (
                <TableRow>
                  <TableCell colSpan={5} align="center">
                    Error loading categories. Please try again.
                  </TableCell>
                </TableRow>
              ) : rows.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={5} align="center">
                    No categories found
                  </TableCell>
                </TableRow>
              ) : (
                rows.map((row, index) => (
                  <TableRow hover key={row.id}>
                    <TableCell>{(page - 1) * limit + index + 1}</TableCell>
                    <TableCell>
                      <Box component="a" className="user_block">
                        <figure>
                          <img 
                            src={row.image?.startsWith('http') ? row.image : `/static/images/${row.image}`} 
                            alt={row.name}
                            onError={(e) => {
                              (e.target as HTMLImageElement).src = "/static/images/placeholder.png";
                            }}
                          />
                        </figure>
                        <p>
                          <b>{row.name}</b>
                        </p>
                      </Box>
                    </TableCell>
                    <TableCell>{row.createdAt}</TableCell>
                    <TableCell>
                      <SwitchToggle 
                        checked={!row.isBlocked} 
                        onChange={() => handleStatusToggle(row.id, row.name, row.isBlocked)}
                        disabled={isUpdatingStatus || isLoading}
                      />
                    </TableCell>
                    <TableCell>
                      <div className="table_actions v2">
                        <IconButton
                          onClick={() => handleEditClick(row)}
                          disabled={isLoading}
                        >
                          <img src="/static/images/edit2_icon.svg" alt="Edit" />
                        </IconButton>
                        <IconButton
                          onClick={() => handleDeleteClick(row)}
                          disabled={isLoading || isDeleting}
                        >
                          <img src="/static/images/trash2_icon.svg" alt="Delete" />
                        </IconButton>
                      </div>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>

        {totalPages > 0 && (
          <div className="table_footer" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '15px 20px', borderTop: '1px solid #e0e0e0' }}>
            <p style={{ margin: 0, color: '#717171', fontSize: '14px' }}>
              Showing <strong>{(page - 1) * limit + 1}</strong> to <strong>{Math.min(page * limit, totalCount)}</strong> of <strong>{totalCount}</strong> entries
            </p>
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
              <Pagination
                module={rows}
                page={page}
                onPageChange={handlePageChange}
                totalPages={totalPages}
                setPage={setPage}
                totalCount={totalCount}
                limit={limit}
              />
            </div>
          </div>
        )}
      </div>

      <AddProductCategory
        open={openModal}
        onClose={handleCloseModal}
        setOpen={setOpenModal}
        editCategory={editCategory}
      />

      <DeleteConfirmationModal
        open={deleteModalOpen}
        onClose={() => {
          setDeleteModalOpen(false);
          setCategoryToDelete(null);
        }}
        setOpen={setDeleteModalOpen}
        onConfirm={handleConfirmDelete}
        itemName={categoryToDelete?.name || "this category"}
        isLoading={isDeleting}
      />
    </>
  );
};

export default ManageProductCategories;
